export type SoundName =
  | 'deal'
  | 'bet'
  | 'chip'
  | 'fold'
  | 'pack'
  | 'win'
  | 'celebration'
  | 'tick'
  | 'click'
  | 'shuffle'
  | 'showdown'
  | 'pot'
  | 'flip';

/** Always audible — not gated by boot/dealing table phase */
const WIN_SEQUENCE_SOUNDS = new Set<SoundName>(['pot', 'win', 'celebration', 'showdown']);

const STORAGE_MUTED = 'tp_sound_muted';
const STORAGE_VOLUME = 'tp_sound_volume';
const STORAGE_MUSIC = 'tp_music_enabled';

/** Ambient table music — kept below SFX so chips/deal sounds stay clear */
const MUSIC_MASTER_GAIN = 0.18;
const MUSIC_OSC_LEVEL = 0.2;

/** Rich procedural casino SFX via Web Audio — no external assets. */
class SoundManager {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private musicOscs: OscillatorNode[] = [];
  private muted = localStorage.getItem(STORAGE_MUTED) === 'true';
  private musicEnabled = localStorage.getItem(STORAGE_MUSIC) === 'true';
  private volume = Math.min(1, Math.max(0, Number(localStorage.getItem(STORAGE_VOLUME) ?? '0.75')));
  private lastTickSecond = -1;
  private unlocked = false;

  private getContext(): AudioContext {
    if (!this.ctx) {
      this.ctx = new AudioContext();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.muted ? 0 : this.volume;
      this.master.connect(this.ctx.destination);
      this.musicGain = this.ctx.createGain();
      this.musicGain.gain.value = this.musicEnabled && !this.muted ? MUSIC_MASTER_GAIN : 0;
      this.musicGain.connect(this.master);
    }
    return this.ctx;
  }

  private output(): GainNode {
    const ctx = this.getContext();
    if (!this.master) {
      this.master = ctx.createGain();
      this.master.connect(ctx.destination);
    }
    this.master.gain.value = this.muted ? 0 : this.volume;
    return this.master;
  }

  unlock(): void {
    if (this.unlocked) return;
    try {
      const ctx = this.getContext();
      if (ctx.state === 'suspended') void ctx.resume();
      this.unlocked = true;
    } catch {
      /* ignore */
    }
  }

  isMuted(): boolean {
    return this.muted;
  }

  isMusicEnabled(): boolean {
    return this.musicEnabled;
  }

  getVolume(): number {
    return this.volume;
  }

  setVolume(v: number): void {
    this.volume = Math.min(1, Math.max(0, v));
    localStorage.setItem(STORAGE_VOLUME, String(this.volume));
    if (this.master) this.master.gain.value = this.muted ? 0 : this.volume;
  }

  setMuted(m: boolean): void {
    this.muted = m;
    localStorage.setItem(STORAGE_MUTED, String(m));
    if (this.master) this.master.gain.value = m ? 0 : this.volume;
    if (m) this.stopAmbientMusic();
    else if (this.musicEnabled) this.startAmbientMusic();
  }

  toggle(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  toggleMusic(): boolean {
    this.musicEnabled = !this.musicEnabled;
    localStorage.setItem(STORAGE_MUSIC, String(this.musicEnabled));
    if (this.musicEnabled && !this.muted) {
      this.unlock();
      this.startAmbientMusic();
    } else {
      this.stopAmbientMusic();
    }
    return this.musicEnabled;
  }

  /** Stop timer tick state — call when turn ends or player acts */
  resetTick(): void {
    this.lastTickSecond = -1;
  }

  /** Stop timer tick + ambient music (e.g. leaving table) */
  stopAll(): void {
    this.resetTick();
    this.stopAmbientMusic();
  }

  private stopAmbientMusic(): void {
    for (const osc of this.musicOscs) {
      try {
        osc.stop();
        osc.disconnect();
      } catch {
        /* already stopped */
      }
    }
    this.musicOscs = [];
    if (this.musicGain) this.musicGain.gain.value = 0;
  }

  private startAmbientMusic(): void {
    if (!this.musicEnabled || this.muted) return;
    this.stopAmbientMusic();
    try {
      const ctx = this.getContext();
      if (!this.musicGain) {
        this.musicGain = ctx.createGain();
        this.musicGain.connect(this.output());
      }
      this.musicGain.gain.value = MUSIC_MASTER_GAIN;

      const freqs = [110, 164.81, 220];
      for (const f of freqs) {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = f;
        g.gain.value = MUSIC_OSC_LEVEL / freqs.length;
        osc.connect(g);
        g.connect(this.musicGain);
        osc.start();
        this.musicOscs.push(osc);
      }
    } catch {
      /* ignore */
    }
  }

  play(name: SoundName): void {
    if (this.muted) return;
    this.unlock();
    try {
      const ctx = this.getContext();
      const dest = this.output();
      const t = ctx.currentTime;

      switch (name) {
        case 'deal':
        case 'flip':
          this.cardFlip(ctx, dest, t, name === 'flip' ? 1 : 0.85);
          break;
        case 'shuffle':
          this.shuffle(ctx, dest, t);
          break;
        case 'chip':
        case 'bet':
        case 'click':
          this.chipClink(ctx, dest, t, name === 'click' ? 0.6 : 1);
          break;
        case 'fold':
        case 'pack':
          this.packFold(ctx, dest, t);
          break;
        case 'tick':
          this.clockTick(ctx, dest, t);
          break;
        case 'showdown':
          this.showdownSting(ctx, dest, t);
          break;
        case 'pot':
          this.potCollect(ctx, dest, t);
          break;
        case 'win':
        case 'celebration':
          this.celebration(ctx, dest, t, name === 'celebration');
          break;
        default:
          break;
      }
    } catch {
      /* ignore audio errors */
    }
  }

  playTick(secondsLeft: number): void {
    if (secondsLeft > 5 || secondsLeft <= 0) return;
    if (secondsLeft === this.lastTickSecond) return;
    this.lastTickSecond = secondsLeft;
    this.play('tick');
  }

  private tone(
    ctx: AudioContext,
    dest: AudioNode,
    t: number,
    freq: number,
    dur: number,
    type: OscillatorType,
    vol: number,
    decay = true
  ): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    gain.gain.setValueAtTime(vol, t);
    if (decay) gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
    osc.connect(gain);
    gain.connect(dest);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }

  private noiseBurst(ctx: AudioContext, dest: AudioNode, t: number, dur: number, vol: number): void {
    const bufferSize = Math.floor(ctx.sampleRate * dur);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 1800;
    filter.Q.value = 0.8;
    gain.gain.setValueAtTime(vol, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
    src.connect(filter);
    filter.connect(gain);
    gain.connect(dest);
    src.start(t);
  }

  private cardFlip(ctx: AudioContext, dest: AudioNode, t: number, intensity: number): void {
    this.noiseBurst(ctx, dest, t, 0.06 * intensity, 0.04 * intensity);
    this.tone(ctx, dest, t + 0.02, 280, 0.08, 'sine', 0.035 * intensity);
    this.tone(ctx, dest, t + 0.04, 520, 0.06, 'triangle', 0.025 * intensity);
  }

  private shuffle(ctx: AudioContext, dest: AudioNode, t: number): void {
    for (let i = 0; i < 5; i++) {
      this.noiseBurst(ctx, dest, t + i * 0.05, 0.04, 0.022);
      this.tone(ctx, dest, t + i * 0.05, 340 + i * 40, 0.05, 'sine', 0.018);
    }
  }

  private chipClink(ctx: AudioContext, dest: AudioNode, t: number, intensity: number): void {
    const freqs = [880, 1320, 1760];
    freqs.forEach((f, i) => {
      this.tone(ctx, dest, t + i * 0.012, f, 0.09, 'sine', 0.028 * intensity);
    });
    this.tone(ctx, dest, t, 2200, 0.05, 'triangle', 0.015 * intensity);
  }

  private packFold(ctx: AudioContext, dest: AudioNode, t: number): void {
    this.tone(ctx, dest, t, 320, 0.12, 'sawtooth', 0.03);
    this.tone(ctx, dest, t + 0.04, 220, 0.18, 'sine', 0.035);
    this.tone(ctx, dest, t + 0.08, 160, 0.22, 'sine', 0.025);
  }

  private clockTick(ctx: AudioContext, dest: AudioNode, t: number): void {
    this.tone(ctx, dest, t, 1200, 0.04, 'square', 0.022);
    this.tone(ctx, dest, t, 600, 0.06, 'sine', 0.012);
  }

  private showdownSting(ctx: AudioContext, dest: AudioNode, t: number): void {
    [220, 277, 330].forEach((f, i) => {
      this.tone(ctx, dest, t + i * 0.08, f, 0.35, 'sine', 0.04);
    });
    this.noiseBurst(ctx, dest, t, 0.15, 0.018);
  }

  private potCollect(ctx: AudioContext, dest: AudioNode, t: number): void {
    this.tone(ctx, dest, t, 180, 0.18, 'sine', 0.028);
    this.noiseBurst(ctx, dest, t + 0.02, 0.12, 0.022);
    const notes = [440, 554, 659, 784, 988, 1175];
    notes.forEach((f, i) => {
      const at = t + 0.06 + i * 0.08;
      this.chipClink(ctx, dest, at, 0.85 + i * 0.04);
      this.tone(ctx, dest, at + 0.01, f, 0.14, 'sine', 0.038);
    });
    this.tone(ctx, dest, t + 0.62, 880, 0.22, 'triangle', 0.042);
  }

  private celebration(ctx: AudioContext, dest: AudioNode, t: number, big: boolean): void {
    const arpeggio = big
      ? [392, 523, 659, 784, 988, 1175, 1319, 1568]
      : [523, 659, 784, 988];
    arpeggio.forEach((f, i) => {
      this.tone(ctx, dest, t + i * 0.09, f, 0.32, 'sine', big ? 0.058 : 0.048);
      this.tone(ctx, dest, t + i * 0.09 + 0.02, f * 2, 0.16, 'triangle', big ? 0.028 : 0.02);
    });
    if (big) {
      this.noiseBurst(ctx, dest, t + 0.35, 0.24, 0.032);
      [1047, 1319, 1568].forEach((f, i) => {
        this.tone(ctx, dest, t + 0.55 + i * 0.08, f, 0.55, 'sine', 0.065);
      });
    }
  }
}

export function isWinSequenceSound(name: SoundName): boolean {
  return WIN_SEQUENCE_SOUNDS.has(name);
}

const soundManager = new SoundManager();
export default soundManager;
